/**
 * Flag-based permission system constants and utilities.
 *
 * Shared between frontend and backend to ensure consistent
 * permission defaults and role auto-scaling logic.
 */
export const SECTION_FLAGS = [
    'canAccessAccounts',
    'canAccessForecast',
    'canAccessDscr',
    'canAccessDocuments',
    'canAccessDeliveryNotes',
    'canAccessOrders',
    'canAccessAdministration',
    'canAccessCommesse',
    'canAccessServiceContracts',
    'canAccessHotel',
    'canAccessWorkReports',
    'canAccessMachines',
    'canAccessRegistry',
    'canAccessProducts',
    'canAccessWarehouse',
    'canAccessPaymentSchedules',
    'canAccessPayments',
    'canAccessSettings',
];
export const ACTION_FLAGS = [
    'canWrite',
    'canExecutePayments',
    'canManageUsers',
    'canConnectBankAccounts',
    'canConnectCassettoFiscale',
    'canExport',
    'canManageBilling',
    'canReconcile',
    'canUseAI',
    'canConnectMcp',
    'canConnectJetHr',
    'canConnectPaypal',
    'canConnectReviso',
    'canManageApiKeys',
    'canApproveWorkReports',
    'canViewAccountBalances',
    'canApproveInvoices',
    'canApproveMachineChecks',
];
export const TECHNICIAN_PERMISSION_FLAGS = [
    'canReadWorkReportProducts',
    'canCreateWorkReportProducts',
    'canCreateWorkReportCustomers',
    'canWriteWorkReportMachines',
    'canReadWorkReportChecks',
    'canWriteWorkReportChecks',
    'canReadWorkReportAttachments',
    'canWriteWorkReportAttachments',
    'canReadWorkReportContracts',
    'canReadWorkReportDeliveryNotes',
    'canReadAllWorkReports',
    'canViewWorkReportPrices',
];
const NO_TECHNICIAN_PERMISSIONS = Object.fromEntries(TECHNICIAN_PERMISSION_FLAGS.map((flag) => [flag, false]));
const ALL_TECHNICIAN_PERMISSIONS = Object.fromEntries(TECHNICIAN_PERMISSION_FLAGS.map((flag) => [flag, true]));
export const PERMISSION_FLAGS = [
    ...SECTION_FLAGS,
    ...ACTION_FLAGS,
    ...TECHNICIAN_PERMISSION_FLAGS,
];
/**
 * Ogni flag concesso: i permessi di chi li possiede tutti (attore OWNER
 * dell'anteprima, SUPERADMIN in `TenantGuard`). Congelato: è condiviso.
 */
export const ALL_PERMISSION_FLAGS_GRANTED = Object.freeze(Object.fromEntries(PERMISSION_FLAGS.map((flag) => [flag, true])));
export const DOCUMENT_SECTION_PERMISSIONS = [
    'canAccessDocuments',
    'canAccessDeliveryNotes',
    'canAccessOrders',
    'canAccessAdministration',
];
export function getDocumentTypePermission(documentType) {
    switch (documentType) {
        case 'DELIVERY_NOTE':
            return 'canAccessDeliveryNotes';
        case 'ORDER':
            return 'canAccessOrders';
        case 'PAYROLL':
        case 'EXPENSE_REPORT':
        case 'LOAN':
        case 'TAX_PAYMENT':
        case 'INPS_CONTRIBUTION':
        case 'INAIL_PREMIUM':
        case 'PA_DEBT':
            return 'canAccessAdministration';
        default:
            return 'canAccessDocuments';
    }
}
export function getAllowedDocumentTypes(permissions) {
    const result = [];
    if (permissions.canAccessDocuments) {
        result.push('INVOICE', 'RECEIPT', 'QUOTE', 'PROFORMA');
    }
    if (permissions.canAccessDeliveryNotes)
        result.push('DELIVERY_NOTE');
    if (permissions.canAccessOrders)
        result.push('ORDER');
    if (permissions.canAccessAdministration) {
        result.push('PAYROLL', 'EXPENSE_REPORT', 'LOAN', 'TAX_PAYMENT', 'INPS_CONTRIBUTION', 'INAIL_PREMIUM', 'PA_DEBT');
    }
    return result;
}
/**
 * Predicati d'azione condivisi FE/BE (piano fix permessi, Fase 4).
 *
 * Un'azione è consentita solo se l'INTERA combinazione di permessi che il
 * backend richiede è presente: il frontend usa questi predicati per mostrare
 * i comandi, il backend per validare le configurazioni utente. Un pulsante
 * mostrato con una combinazione diversa da quella richiesta dal server
 * produce 403 a runtime — ogni nuova azione aggiunge qui il suo predicato.
 */
export function hasAnyDocumentSection(flags) {
    return DOCUMENT_SECTION_PERMISSIONS.some((permission) => flags[permission] === true);
}
/**
 * Riconciliare richiede il flag dedicato, i Conti (i movimenti sono metà del
 * match) e almeno una sezione documentale (l'altra metà). Allineato a
 * reconciliation.controller.ts.
 */
export function canUseReconciliation(flags) {
    return (flags.canReconcile === true &&
        flags.canAccessAccounts === true &&
        hasAnyDocumentSection(flags));
}
/**
 * Import PDF/CSV di estratti conto: allineato a bank-import.controller.ts e
 * pdf-import.controller.ts (`canWrite` + `canAccessAccounts`).
 * `canConnectBankAccounts` NON c'entra: quel flag copre solo il collegamento
 * open banking di nuovi conti.
 */
export function canImportBankStatements(flags) {
    return flags.canWrite === true && flags.canAccessAccounts === true;
}
/**
 * Collegare nuovi conti via open banking / crearli: allineato a
 * accounts.controller.ts (`canAccessAccounts` + `canWrite` +
 * `canConnectBankAccounts`).
 */
export function canConnectNewBankAccounts(flags) {
    return (flags.canConnectBankAccounts === true &&
        flags.canWrite === true &&
        flags.canAccessAccounts === true);
}
/**
 * Letture che partono dai saldi dei conti: ogni insieme di flag è definito
 * una volta sola e da qui derivano sia il predicato (BE e FE) sia l'elenco
 * dei flag mancanti da spiegare all'utente. I predicati ricevono i flag
 * EFFETTIVI: il bypass OWNER/SUPERADMIN si risolve a monte (backend:
 * `effectivePermissionFlags`; webapp: `usePermissions`).
 */
export function findMissingPermissionFlags(flags, required) {
    return required.filter((flag) => flags[flag] !== true);
}
export function hasAllPermissionFlags(flags, required) {
    return findMissingPermissionFlags(flags, required).length === 0;
}
/**
 * Flag effettivi di una membership di cui si conoscono solo ruolo e flag
 * (es. i clienti del pannello consulente): l'OWNER li ha tutti, come nelle
 * guard. Il backend con la request usa `effectivePermissionFlags`, che
 * risolve anche il SUPERADMIN; la webapp per l'utente corrente `usePermissions`.
 */
export function effectiveTenantPermissionFlags({ tenantRole, permissions, }) {
    if (tenantRole === 'OWNER')
        return ALL_PERMISSION_FLAGS_GRANTED;
    return permissions ?? {};
}
/** Liquidità (saldi aggregati dei conti): Conti + saldi. */
export const LIQUIDITY_REQUIRED_FLAGS = [
    'canAccessAccounts',
    'canViewAccountBalances',
];
/**
 * Previsionale di cassa: parte dai saldi dei conti. Allineato a
 * cash-flow-forecast.controller.ts.
 */
export const CASH_FLOW_FORECAST_REQUIRED_FLAGS = [
    'canAccessAccounts',
    'canAccessForecast',
    'canViewAccountBalances',
];
/** DSCR e allerta crisi: allineato a dscr.controller.ts. */
export const DSCR_REQUIRED_FLAGS = [
    'canAccessDscr',
    'canViewAccountBalances',
];
/**
 * Azioni sui saldi iniziali (conferma, ricalcolo, completamento, reset):
 * allineato a onboarding.controller.ts.
 */
export const BALANCE_ONBOARDING_REQUIRED_FLAGS = [
    'canAccessSettings',
    'canWrite',
    'canViewAccountBalances',
];
/**
 * Export prima nota: riporta il saldo progressivo del conto. Allineato a
 * cash-movements.controller.ts (classe `canAccessAccounts` + endpoint
 * `canExport` + `canViewAccountBalances`).
 */
export const PRIMA_NOTA_EXPORT_REQUIRED_FLAGS = [
    'canAccessAccounts',
    'canExport',
    'canViewAccountBalances',
];
/**
 * «Gestisci API e webhook»: le chiavi leggono la public API (saldi inclusi)
 * senza i flag dell'utente che le crea. Allineato a
 * public-api-management.controller.ts e public-api-docs.controller.ts; copre
 * anche i membri con il flag chiavi ma senza saldi (stato pre-backfill).
 */
export const PUBLIC_API_MANAGEMENT_REQUIRED_FLAGS = [
    'canManageApiKeys',
    'canViewAccountBalances',
];
export function canManagePublicApiKeys(flags) {
    return hasAllPermissionFlags(flags, PUBLIC_API_MANAGEMENT_REQUIRED_FLAGS);
}
export function canExportPrimaNota(flags) {
    return hasAllPermissionFlags(flags, PRIMA_NOTA_EXPORT_REQUIRED_FLAGS);
}
export function canViewLiquidity(flags) {
    return hasAllPermissionFlags(flags, LIQUIDITY_REQUIRED_FLAGS);
}
export function canViewCashFlowForecast(flags) {
    return hasAllPermissionFlags(flags, CASH_FLOW_FORECAST_REQUIRED_FLAGS);
}
export function canViewDscr(flags) {
    return hasAllPermissionFlags(flags, DSCR_REQUIRED_FLAGS);
}
export function canManageBalanceOnboarding(flags) {
    return hasAllPermissionFlags(flags, BALANCE_ONBOARDING_REQUIRED_FLAGS);
}
/**
 * Requisiti di ogni KPI, nella stessa forma delle dipendenze fra permessi:
 * `requires` tutti, di `requiresAny` almeno uno. Le scadenze si calcolano
 * sulle rate dei soli tipi di documento visibili: senza nessuna sezione
 * documentale il perimetro è vuoto e il KPI varrebbe uno zero finto.
 */
export const ADVISOR_KPI_REQUIREMENTS = {
    liquidity: { requires: LIQUIDITY_REQUIRED_FLAGS },
    cashflow: { requires: ['canAccessAccounts'] },
    documents: { requires: ['canAccessDocuments'] },
    upcomingSchedules: {
        requires: ['canAccessPaymentSchedules'],
        requiresAny: DOCUMENT_SECTION_PERMISSIONS,
    },
    reconciliation: { requires: ['canReconcile'] },
    crisis: { requires: DSCR_REQUIRED_FLAGS },
};
/** Cosa manca per vedere un KPI: unica fonte per il controllo e per i messaggi. */
export function findMissingAdvisorKpiRequirements(flags, kpi) {
    return findUnmetPermissionDependency(flags, ADVISOR_KPI_REQUIREMENTS[kpi]);
}
export function canViewAdvisorKpi(flags, kpi) {
    return isPermissionDependencyMet(findMissingAdvisorKpiRequirements(flags, kpi));
}
/**
 * Valuta una dipendenza: unica regola per i flag e per i KPI del pannello.
 * Un `requiresAny` dichiarato non è mai vuoto (contratto verificato dai test
 * sulle mappe): un gruppo senza alternative non si potrebbe né spiegare né
 * soddisfare.
 */
export function findUnmetPermissionDependency(flags, { requires, requiresAny = [] }) {
    const anyOfMet = requiresAny.length === 0 ||
        requiresAny.some((flag) => flags[flag] === true);
    return {
        missing: findMissingPermissionFlags(flags, requires),
        missingAnyOf: anyOfMet ? [] : [...requiresAny],
    };
}
/** Dipendenza soddisfatta: l'unico modo di leggere l'esito di `findUnmetPermissionDependency`. */
export function isPermissionDependencyMet({ missing, missingAnyOf, }) {
    return missing.length === 0 && missingAnyOf.length === 0;
}
export const PERMISSION_DEPENDENCIES = {
    canCreateWorkReportProducts: { requires: ['canReadWorkReportProducts'] },
    canWriteWorkReportMachines: { requires: ['canAccessMachines'] },
    canReadWorkReportChecks: { requires: ['canAccessMachines'] },
    canWriteWorkReportChecks: { requires: ['canReadWorkReportChecks'] },
    canReadWorkReportAttachments: { requires: ['canAccessMachines'] },
    canWriteWorkReportAttachments: { requires: ['canReadWorkReportAttachments'] },
    // Riconciliare = collegare movimenti (Conti) a documenti (almeno una
    // sezione documentale). Allineato a reconciliation.controller.ts.
    canReconcile: {
        requires: ['canAccessAccounts'],
        requiresAny: [...DOCUMENT_SECTION_PERMISSIONS],
    },
    // Collegare conti open banking crea/modifica conti:
    // accounts.controller.ts richiede la terna completa.
    canConnectBankAccounts: {
        requires: ['canAccessAccounts', 'canWrite'],
    },
    // Collegare PayPal crea conti e ne importa i movimenti: stessa terna del
    // collegamento open banking (paypal.controller.ts richiede anche canWrite).
    canConnectPaypal: {
        requires: ['canAccessAccounts', 'canWrite'],
    },
    // Eseguire pagamenti presuppone la sezione Pagamenti e i Conti.
    canExecutePayments: {
        requires: ['canAccessPayments', 'canAccessAccounts'],
    },
    // Approvare congela i dati economici e registra addebiti: presuppone la
    // sezione Rapportini e la scrittura. Allineato a work-reports.controller.ts.
    canApproveWorkReports: {
        requires: ['canAccessWorkReports', 'canWrite'],
    },
    // Approvare una fattura d'acquisto arrivata la manda a «Da pagare»:
    // presuppone la sezione Fatture e la scrittura. Allineato a
    // incoming-review.controller.ts.
    canApproveInvoices: {
        requires: ['canAccessDocuments', 'canWrite'],
    },
    // Approvare una verifica tecnica chiude il ciclo del piano e congela le
    // risposte: presuppone la sezione Macchine e la scrittura. Allineato a
    // machine-checks.controller.ts.
    canApproveMachineChecks: {
        requires: ['canAccessMachines', 'canWrite'],
    },
    // Saldi, fido e totali dei conti: sotto-permesso di Conti. Senza, i
    // movimenti restano visibili (servono a riconciliare) ma ogni riga Account
    // arriva con i campi saldo a null (AccountBalanceRedactionInterceptor).
    canViewAccountBalances: {
        requires: ['canAccessAccounts'],
    },
    // Il DSCR parte dalla liquidità attuale: senza saldi la rivelerebbe. Il
    // Previsionale no: Pianificazione e Ricorrenze mostrano solo flussi, e la
    // vista di cassa (che parte dal saldo) è governata da
    // CASH_FLOW_FORECAST_REQUIRED_FLAGS.
    canAccessDscr: {
        requires: ['canViewAccountBalances'],
    },
    // Le chiavi API leggono la public API (saldi inclusi) senza guardare i flag
    // dell'utente che le ha create: chi non vede i saldi non può crearle.
    canManageApiKeys: {
        requires: ['canViewAccountBalances'],
    },
};
export function findPermissionDependencyViolations(flags) {
    const violations = [];
    for (const [flag, dependency] of Object.entries(PERMISSION_DEPENDENCIES)) {
        if (flags[flag] !== true)
            continue;
        const unmet = findUnmetPermissionDependency(flags, dependency);
        if (!isPermissionDependencyMet(unmet)) {
            violations.push({
                flag,
                missing: unmet.missing,
                ...(unmet.missingAnyOf.length > 0 && {
                    missingAnyOf: unmet.missingAnyOf,
                }),
            });
        }
    }
    return violations;
}
/**
 * Propaga un toggle rispettando le dipendenze: attivando un flag si attivano
 * i prerequisiti mancanti (per `requiresAny` il primo elencato, che il form
 * mostra e l'utente può cambiare); disattivando un prerequisito si spengono
 * a cascata i flag che ne dipendevano. Pura: restituisce un nuovo oggetto.
 */
export function applyPermissionDependencies(flags, changedFlag, enabled) {
    const next = { ...flags, [changedFlag]: enabled };
    for (let guard = 0; guard < PERMISSION_FLAGS.length; guard += 1) {
        const violations = findPermissionDependencyViolations(next);
        if (violations.length === 0)
            break;
        for (const violation of violations) {
            if (enabled) {
                for (const required of violation.missing)
                    next[required] = true;
                if (violation.missingAnyOf)
                    next[violation.missingAnyOf[0]] = true;
            }
            else {
                next[violation.flag] = false;
            }
        }
    }
    return next;
}
export const ROLE_HIERARCHY = {
    OWNER: 4,
    ADMIN: 3,
    USER: 2,
    GUEST: 1,
};
const OWNER_DEFAULTS = {
    ...ALL_TECHNICIAN_PERMISSIONS,
    canAccessAccounts: true,
    canAccessForecast: true,
    canAccessDscr: true,
    canAccessDocuments: true,
    canAccessDeliveryNotes: true,
    canAccessOrders: true,
    canAccessAdministration: true,
    canAccessCommesse: true,
    canAccessServiceContracts: true,
    canAccessHotel: true,
    canAccessWorkReports: true,
    canAccessMachines: true,
    canAccessRegistry: true,
    canAccessProducts: true,
    canAccessWarehouse: true,
    canAccessPaymentSchedules: true,
    canAccessPayments: true,
    canAccessSettings: true,
    canWrite: true,
    canExecutePayments: true,
    canManageUsers: true,
    canConnectBankAccounts: true,
    canConnectCassettoFiscale: true,
    canExport: true,
    canManageBilling: true,
    canReconcile: true,
    canUseAI: true,
    canConnectMcp: true,
    canConnectJetHr: true,
    canConnectPaypal: true,
    canConnectReviso: true,
    canManageApiKeys: true,
    canApproveWorkReports: true,
    canViewAccountBalances: true,
    canApproveInvoices: true,
    canApproveMachineChecks: true,
};
const ADMIN_DEFAULTS = {
    ...ALL_TECHNICIAN_PERMISSIONS,
    canAccessAccounts: true,
    canAccessForecast: true,
    canAccessDscr: true,
    canAccessDocuments: true,
    canAccessDeliveryNotes: true,
    canAccessOrders: true,
    canAccessAdministration: true,
    canAccessCommesse: true,
    canAccessServiceContracts: true,
    canAccessHotel: true,
    canAccessWorkReports: true,
    canAccessMachines: true,
    canAccessRegistry: true,
    canAccessProducts: true,
    canAccessWarehouse: true,
    canAccessPaymentSchedules: true,
    canAccessPayments: true,
    canAccessSettings: true,
    canWrite: true,
    canExecutePayments: true,
    canManageUsers: true,
    canConnectBankAccounts: true,
    canConnectCassettoFiscale: true,
    canExport: true,
    canManageBilling: true,
    canReconcile: true,
    canUseAI: true,
    canConnectMcp: true,
    canConnectJetHr: true,
    canConnectPaypal: true,
    canConnectReviso: true,
    canManageApiKeys: true,
    canApproveWorkReports: true,
    canViewAccountBalances: true,
    canApproveInvoices: true,
    canApproveMachineChecks: true,
};
const USER_DEFAULTS = {
    ...NO_TECHNICIAN_PERMISSIONS,
    canAccessAccounts: false,
    canAccessForecast: false,
    canAccessDscr: false,
    canAccessDocuments: true,
    canAccessDeliveryNotes: true,
    canAccessOrders: true,
    canAccessAdministration: true,
    canAccessCommesse: true,
    canAccessServiceContracts: false,
    canAccessHotel: false,
    canAccessWorkReports: true,
    canAccessMachines: true,
    canAccessRegistry: true,
    canAccessProducts: true,
    canAccessWarehouse: true,
    canAccessPaymentSchedules: true,
    canAccessPayments: false,
    canAccessSettings: false,
    canWrite: true,
    canExecutePayments: false,
    canManageUsers: false,
    canConnectBankAccounts: false,
    canConnectCassettoFiscale: false,
    canExport: false,
    canManageBilling: false,
    canReconcile: false,
    canUseAI: false,
    canConnectMcp: false,
    canConnectJetHr: false,
    canConnectPaypal: false,
    canConnectReviso: false,
    canManageApiKeys: false,
    canApproveWorkReports: false,
    canViewAccountBalances: false,
    canApproveInvoices: false,
    canApproveMachineChecks: false,
};
const GUEST_DEFAULTS = {
    ...NO_TECHNICIAN_PERMISSIONS,
    canAccessAccounts: true,
    canAccessForecast: true,
    canAccessDscr: true,
    canAccessDocuments: false,
    canAccessDeliveryNotes: false,
    canAccessOrders: false,
    canAccessAdministration: false,
    canAccessCommesse: false,
    canAccessServiceContracts: false,
    canAccessHotel: false,
    canAccessWorkReports: false,
    canAccessMachines: false,
    canAccessRegistry: false,
    canAccessProducts: false,
    canAccessWarehouse: false,
    canAccessPaymentSchedules: false,
    canAccessPayments: true,
    canAccessSettings: false,
    canWrite: false,
    canExecutePayments: false,
    canManageUsers: false,
    canConnectBankAccounts: false,
    canConnectCassettoFiscale: false,
    canExport: false,
    canManageBilling: false,
    canReconcile: false,
    canUseAI: false,
    canConnectMcp: false,
    canConnectJetHr: false,
    canConnectPaypal: false,
    canConnectReviso: false,
    canManageApiKeys: false,
    canApproveWorkReports: false,
    canViewAccountBalances: true,
    canApproveInvoices: false,
    canApproveMachineChecks: false,
};
export const ROLE_PERMISSION_DEFAULTS = {
    OWNER: OWNER_DEFAULTS,
    ADMIN: ADMIN_DEFAULTS,
    USER: USER_DEFAULTS,
    GUEST: GUEST_DEFAULTS,
};
/**
 * Preset suggerito per il mandato advisor (commercialista).
 * NON è un quinto ruolo: è solo il default proposto dalla UI quando si
 * invita un advisor (relationshipType = ADVISOR). Il ruolo effettivo
 * auto-scala via computeRoleFromFlags(). Vedi docs/ADVISOR_PANEL.md §3.4.
 */
export const ADVISOR_SUGGESTED_FLAGS = {
    ...NO_TECHNICIAN_PERMISSIONS,
    canAccessAccounts: true,
    canAccessForecast: true,
    canAccessDscr: true,
    canAccessDocuments: true,
    canAccessDeliveryNotes: true,
    canAccessOrders: true,
    canAccessAdministration: true,
    canAccessCommesse: true,
    canAccessServiceContracts: false,
    canAccessHotel: false,
    canAccessWorkReports: false,
    canAccessMachines: false,
    canAccessRegistry: true,
    canAccessProducts: true,
    canAccessWarehouse: true,
    canAccessPaymentSchedules: true,
    canAccessPayments: true,
    canAccessSettings: false,
    canWrite: true,
    canExecutePayments: false,
    canManageUsers: false,
    canConnectBankAccounts: false,
    canConnectCassettoFiscale: false,
    canExport: true,
    canManageBilling: false,
    canReconcile: true,
    canUseAI: true,
    canConnectMcp: false,
    canConnectJetHr: false,
    canConnectPaypal: false,
    canConnectReviso: false,
    canManageApiKeys: false,
    canApproveWorkReports: false,
    canViewAccountBalances: true,
    canApproveInvoices: false,
    canApproveMachineChecks: false,
};
/** Returns the default permission flags for a given role. */
export function getDefaultPermissions(role) {
    return { ...(ROLE_PERMISSION_DEFAULTS[role] ?? GUEST_DEFAULTS) };
}
/**
 * Computes the highest role (excluding OWNER) whose defaults
 * are entirely contained in the given flags.
 *
 * OWNER is excluded from auto-scaling — it can only be assigned manually.
 */
export function computeRoleFromFlags(flags) {
    const matchesDefaults = (defaults) => [...SECTION_FLAGS, ...ACTION_FLAGS].every((flag) => !defaults[flag] || flags[flag]);
    if (matchesDefaults(ADMIN_DEFAULTS))
        return 'ADMIN';
    if (matchesDefaults(USER_DEFAULTS))
        return 'USER';
    if (matchesDefaults(GUEST_DEFAULTS))
        return 'GUEST';
    return 'GUEST';
}
/** Returns true if roleA outranks roleB in the hierarchy. */
export function isHigherRank(roleA, roleB) {
    return (ROLE_HIERARCHY[roleA] ?? 0) > (ROLE_HIERARCHY[roleB] ?? 0);
}
/** Mirrors the backend hierarchy check used by tenant membership actions. */
export function canManageTenantRole(requesterRole, targetRole, isSelf = false) {
    if (isSelf)
        return true;
    if (!requesterRole || !targetRole)
        return false;
    return requesterRole === 'OWNER' || isHigherRank(requesterRole, targetRole);
}
/**
 * Separates permission flags from other fields in a DTO-like object.
 * Returns `permissions` (only defined boolean flags) and `rest` (everything else).
 */
export function extractPermissionFlags(dto) {
    const permissions = {};
    const rest = {};
    for (const [key, value] of Object.entries(dto)) {
        if (value === undefined)
            continue;
        if (PERMISSION_FLAGS.includes(key)) {
            permissions[key] = value;
        }
        else {
            rest[key] = value;
        }
    }
    return { permissions, rest };
}
const LEGACY_SECTION_FALLBACKS = {
    canAccessDscr: 'canAccessForecast',
    canAccessDeliveryNotes: 'canAccessDocuments',
    canAccessOrders: 'canAccessDocuments',
    canAccessAdministration: 'canAccessDocuments',
    canAccessCommesse: 'canAccessDocuments',
    canAccessProducts: 'canAccessRegistry',
    canAccessWarehouse: 'canAccessRegistry',
    canAccessPayments: 'canAccessAccounts',
};
/** Resolves nullable V2 flags through their historical umbrella permission. */
export function resolvePermissionFlag(source, flag) {
    const values = source;
    const directValue = values?.[flag];
    if (typeof directValue === 'boolean')
        return directValue;
    if (flag === 'canReadWorkReportProducts')
        return values?.canAccessWorkReports === true;
    if (flag === 'canCreateWorkReportCustomers')
        return values?.canAccessWorkReports === true && values?.canWrite === true;
    if (flag === 'canWriteWorkReportMachines')
        return values?.canAccessMachines === true && values?.canWrite === true;
    if (flag === 'canReadWorkReportChecks')
        return values?.canAccessMachines === true;
    if (flag === 'canWriteWorkReportChecks')
        return values?.canAccessMachines === true && values?.canWrite === true;
    if (flag === 'canReadWorkReportAttachments')
        return values?.canAccessMachines === true;
    if (flag === 'canWriteWorkReportAttachments')
        return values?.canAccessMachines === true && values?.canWrite === true;
    if (flag === 'canReadAllWorkReports')
        return values?.canAccessWorkReports === true;
    if (TECHNICIAN_PERMISSION_FLAGS.includes(flag) &&
        values?.tenantRole === 'ADMIN')
        return true;
    const fallbackFlag = LEGACY_SECTION_FALLBACKS[flag];
    return fallbackFlag ? values?.[fallbackFlag] === true : false;
}
/** Picks the effective permission values from a TenantUser-like object. */
export function pickPermissionFlags(source) {
    return Object.fromEntries(PERMISSION_FLAGS.map((flag) => [flag, resolvePermissionFlag(source, flag)]));
}
/**
 * Sezioni e azioni che un posto tecnico può avere: Rapportini e Macchine
 * (consultare le macchine dei clienti e compilarne le verifiche è lavoro da
 * tecnico), più la scrittura per compilarli. Approvare resta da utente.
 */
export const TECHNICIAN_SEAT_FLAGS = [
    'canAccessWorkReports',
    'canAccessMachines',
    'canWrite',
    ...TECHNICIAN_PERMISSION_FLAGS,
];
/**
 * Preset del tecnico (listino 2026-10): Rapportini e Macchine, con scrittura
 * per compilarli. Occupa un posto tecnico, non un posto utente.
 */
export const TECHNICIAN_SUGGESTED_FLAGS = Object.freeze(Object.fromEntries(PERMISSION_FLAGS.map((flag) => [
    flag,
    [
        'canAccessWorkReports',
        'canAccessMachines',
        'canWrite',
        'canReadWorkReportProducts',
        'canWriteWorkReportMachines',
        'canReadWorkReportChecks',
        'canWriteWorkReportChecks',
        'canReadWorkReportAttachments',
        'canWriteWorkReportAttachments',
    ].includes(flag),
])));
/** Filtro degli elenchi Utenti e Inviti per tipo di posto (`?seat=`). */
export const SEAT_FILTERS = {
    USER: 'USER',
    TECHNICIAN: 'TECHNICIAN',
};
/** Stato di accesso all'app di un posto tecnico nell'elenco Tecnici. */
export const TECHNICIAN_SEAT_STATUSES = {
    ACTIVE: 'ACTIVE',
    INVITED: 'INVITED',
    INVITATION_EXPIRED: 'INVITATION_EXPIRED',
};
/**
 * Il commercialista esterno (mandato `ADVISOR`) non occupa posti: né utente
 * né tecnico. Il suo accesso dipende dal mandato, non dal piano del cliente.
 */
export function isSeatExempt(member) {
    return member.relationshipType === 'ADVISOR';
}
/**
 * Un membro occupa un posto tecnico se vede i Rapportini e al più le
 * Macchine: nessun'altra sezione e nessuna azione oltre alla scrittura
 * (`TECHNICIAN_SEAT_FLAGS`). OWNER, ADMIN e advisor restano sempre utenti.
 */
export function isTechnicianSeat(member) {
    if (member.role === 'OWNER' || member.role === 'ADMIN')
        return false;
    if (member.relationshipType === 'ADVISOR')
        return false;
    if (member.flags.canAccessWorkReports !== true)
        return false;
    return PERMISSION_FLAGS.every((flag) => TECHNICIAN_SEAT_FLAGS.includes(flag) || member.flags[flag] !== true);
}
//# sourceMappingURL=permissions.js.map